import axiosInstance from './AxiosInstance';
import { formatDateToDDMMMYYYY } from '../utils/dateFormatter';

const PER_MODULE_LIMIT = 5;

function getCorporateId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.corporateId || user?.id || null;
  } catch {
    return null;
  }
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function pickList(payload, keys) {
  if (!payload) return [];
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
    if (Array.isArray(payload?.data?.[key])) return payload.data[key];
  }
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}

function formatTaskDueDate(task) {
  return formatDateToDDMMMYYYY(
    task?.due_date || task?.EndDate || task?.end_date || task?.Deadline
  );
}

function formatTaskSearchSubtitle(task, extras = []) {
  const due = formatTaskDueDate(task);
  return [
    ...extras,
    task?.priority || task?.Priority,
    due ? `Due ${due}` : '',
  ]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' · ');
}

function todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function documentTitle(d) {
  return (
    d?.document_title ||
    d?.title ||
    d?.document_name ||
    d?.name ||
    d?.file_name ||
    'Untitled document'
  );
}

function mapDocumentItem(d) {
  return {
    id: d.id,
    title: documentTitle(d),
    subtitle:
      d.reference_number ||
      d.category?.name ||
      d.category_name ||
      (d.expiry_date ? `Expires ${formatDateToDDMMMYYYY(d.expiry_date)}` : ''),
    to: `/documents/details/${d.id}`,
  };
}

function flattenFavoriteDocuments(payload) {
  const data = payload?.data ?? payload;
  if (Array.isArray(data) && data.length > 0 && data[0]?.category !== undefined) {
    return data.flatMap((group) => group.documents || []);
  }
  return pickList(payload, ['documents', 'items', 'data']);
}

function matchesQuery(value, q) {
  return String(value || '').toLowerCase().includes(String(q || '').toLowerCase());
}

function asGroups(groups) {
  return { groups: asArray(groups).filter((g) => g?.items?.length) };
}

/**
 * Search across main modules for the dashboard global search panel.
 * @param {string} query
 * @returns {Promise<{ groups: Array<{ key: string, title: string, viewAllTo: string, items: Array<{ id: string|number, title: string, subtitle?: string, to: string }> }> }>}
 */
export async function searchAcrossModules(query) {
  const q = String(query || '').trim();
  if (q.length < 2) {
    return { groups: [] };
  }

  const corporateId = getCorporateId();
  const encoded = encodeURIComponent(q);

  const [tasksRes, projectsRes, forumsRes, docsRes, vaultRes] = await Promise.allSettled([
    axiosInstance.get('/task/list', {
      params: { search: q, limit: PER_MODULE_LIMIT, page: 1, include_project_tasks: true },
    }),
    axiosInstance.get('/project/list', { params: { search: q, limit: PER_MODULE_LIMIT, page: 1 } }),
    axiosInstance.get('/forum/forums', { params: { search: q, limit: PER_MODULE_LIMIT, page: 1 } }),
    corporateId
      ? axiosInstance.get('/documents/list', {
          params: { search: q, corporate_id: corporateId, limit: PER_MODULE_LIMIT, page: 1 },
        })
      : Promise.reject(new Error('missing corporate_id')),
    axiosInstance.get('/credential-vault', { params: { search: q, limit: PER_MODULE_LIMIT, page: 1 } }),
  ]);

  const groups = [];

  if (tasksRes.status === 'fulfilled') {
    const tasks = pickList(tasksRes.value?.data, ['tasks', 'data']).slice(0, PER_MODULE_LIMIT);
    if (tasks.length) {
      groups.push({
        key: 'tasks',
        title: 'Tasks',
        viewAllTo: `/task/tasklist?search=${encoded}`,
        items: tasks.map((t) => ({
          id: t.id || t.taskid,
          title: t.title || t.task_name || 'Untitled task',
          subtitle: formatTaskSearchSubtitle(
            t,
            t.project_id ? ['Project task'] : [t.status_label]
          ),
          to: `/task/taskdetails/${t.id || t.taskid}`,
        })),
      });
    }
  }

  if (projectsRes.status === 'fulfilled') {
    const projects = pickList(projectsRes.value?.data, ['projects', 'data']).slice(0, PER_MODULE_LIMIT);
    if (projects.length) {
      groups.push({
        key: 'projects',
        title: 'Projects',
        viewAllTo: `/task/projectlist?search=${encoded}`,
        items: projects.map((p) => ({
          id: p.id,
          title: p.title || p.name || 'Untitled project',
          subtitle: p.status != null ? `Status ${p.status}` : '',
          to: `/task/projectdetails/${p.id}`,
        })),
      });
    }
  }

  if (forumsRes.status === 'fulfilled') {
    const forums = pickList(forumsRes.value?.data, ['items', 'forums', 'data']).slice(0, PER_MODULE_LIMIT);
    if (forums.length) {
      groups.push({
        key: 'forum',
        title: 'Forum',
        viewAllTo: `/forum/list?search=${encoded}`,
        items: forums.map((f) => ({
          id: f.id,
          title: f.title || 'Untitled discussion',
          subtitle: f.status || '',
          to: `/forum/detail/${f.id}`,
        })),
      });
    }
  }

  if (docsRes.status === 'fulfilled') {
    const docs = pickList(docsRes.value?.data?.data || docsRes.value?.data, ['documents', 'data']).slice(
      0,
      PER_MODULE_LIMIT
    );
    if (docs.length) {
      groups.push({
        key: 'documents',
        title: 'Documents',
        viewAllTo: `/documents/mydocuments?search=${encoded}`,
        items: docs.map((d) => mapDocumentItem(d)),
      });
    }
  }

  if (vaultRes.status === 'fulfilled') {
    const credentials = pickList(vaultRes.value?.data, ['credentials', 'items', 'data']).slice(
      0,
      PER_MODULE_LIMIT
    );
    if (credentials.length) {
      groups.push({
        key: 'vault',
        title: 'Vault',
        viewAllTo: `/credential-vault/credentialVaultList?search=${encoded}`,
        items: credentials.map((c) => ({
          id: c.id,
          title: c.title || c.name || c.username || 'Credential',
          subtitle: c.category || c.scope || '',
          to: `/credential-vault/credentialDetail/${c.id}`,
        })),
      });
    }
  }

  return { groups: asArray(groups) };
}

/**
 * Search projects and project-linked tasks for the Projects module navbar.
 */
export async function searchProjectsModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) {
    return { groups: [] };
  }

  const encoded = encodeURIComponent(q);
  const [projectsRes, tasksRes] = await Promise.allSettled([
    axiosInstance.get('/project/list', { params: { search: q, limit: PER_MODULE_LIMIT, page: 1 } }),
    axiosInstance.get('/task/list', {
      params: { search: q, limit: PER_MODULE_LIMIT, page: 1, only_project_tasks: true },
    }),
  ]);

  const groups = [];

  if (projectsRes.status === 'fulfilled') {
    const projects = pickList(projectsRes.value?.data, ['projects', 'data']).slice(0, PER_MODULE_LIMIT);
    if (projects.length) {
      groups.push({
        key: 'projects',
        title: 'Projects',
        viewAllTo: `/task/projectlist?search=${encoded}`,
        items: projects.map((p) => ({
          id: p.id,
          title: p.title || p.name || 'Untitled project',
          subtitle: p.status != null ? `Status ${p.status}` : '',
          to: `/task/projectdetails/${p.id}`,
        })),
      });
    }
  }

  if (tasksRes.status === 'fulfilled') {
    const tasks = pickList(tasksRes.value?.data, ['tasks', 'data']).slice(0, PER_MODULE_LIMIT);
    if (tasks.length) {
      groups.push({
        key: 'project-tasks',
        title: 'Project Tasks',
        viewAllTo: `/task/projecttasklist?search=${encoded}`,
        items: tasks.map((t) => ({
          id: t.id || t.taskid,
          title: t.title || t.task_name || 'Untitled task',
          subtitle: formatTaskSearchSubtitle(t, [
            t.project_title || t.project_name || 'Project task',
          ]),
          to: `/task/taskdetails/${t.id || t.taskid}`,
        })),
      });
    }
  }

  return { groups: asArray(groups) };
}

/**
 * Search discussions for the Forum module navbar.
 */
export async function searchForumModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) {
    return { groups: [] };
  }

  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/forum/forums', {
      params: { search: q, limit: PER_MODULE_LIMIT, page: 1 },
    });
    const forums = pickList(res?.data, ['items', 'forums', 'data']).slice(0, PER_MODULE_LIMIT);
    if (!forums.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'forum',
          title: 'Discussions',
          viewAllTo: `/forum/list?search=${encoded}`,
          items: forums.map((f) => ({
            id: f.id,
            title: f.title || 'Untitled discussion',
            subtitle: (f.status || '').replace('_', ' '),
            to: `/forum/detail/${f.id}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

/**
 * Search documents for the Documents dashboard navbar.
 */
export async function searchDocumentsModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) {
    return { groups: [] };
  }

  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };

  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/documents/list', {
      params: { search: q, corporate_id: corporateId, limit: PER_MODULE_LIMIT, page: 1 },
    });
    const docs = pickList(res?.data?.data || res?.data, ['documents', 'data']).slice(
      0,
      PER_MODULE_LIMIT
    );
    if (!docs.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'documents',
          title: 'Documents',
          viewAllTo: `/documents/mydocuments?search=${encoded}`,
          items: docs.map((d) => mapDocumentItem(d)),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchTasksModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/task/list', {
      params: { search: q, limit: PER_MODULE_LIMIT, page: 1, include_project_tasks: true },
    });
    const tasks = pickList(res?.data, ['tasks', 'data']).slice(0, PER_MODULE_LIMIT);
    if (!tasks.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'tasks',
          title: 'Tasks',
          viewAllTo: `/task/tasklist?search=${encoded}`,
          items: tasks.map((t) => ({
            id: t.id || t.taskid,
            title: t.title || t.task_name || 'Untitled task',
            subtitle: formatTaskSearchSubtitle(
              t,
              t.project_id ? ['Project task'] : [t.status_label]
            ),
            to: `/task/taskdetails/${t.id || t.taskid}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchWorksheetModule(query) {
  const result = await searchTasksModule(query);
  const encoded = encodeURIComponent(String(query || '').trim());
  return {
    groups: (result.groups || []).map((group) => ({
      ...group,
      viewAllTo: `/task/worksheet?search=${encoded}`,
    })),
  };
}

export async function searchSharedDocumentsModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  const encoded = encodeURIComponent(q);

  const [withMeRes, byMeRes] = await Promise.allSettled([
    axiosInstance.get('/documents/internal/shared', {
      params: { search: q, corporate_id: corporateId, limit: PER_MODULE_LIMIT, page: 1 },
    }),
    axiosInstance.get('/documents/shared-by-me', {
      params: { search: q, corporate_id: corporateId, limit: PER_MODULE_LIMIT, page: 1 },
    }),
  ]);

  const groups = [];
  if (withMeRes.status === 'fulfilled') {
    const docs = pickList(withMeRes.value?.data?.data || withMeRes.value?.data, [
      'documents',
      'items',
      'data',
    ]).slice(0, PER_MODULE_LIMIT);
    if (docs.length) {
      groups.push({
        key: 'shared-with-me',
        title: 'Shared With Me',
        viewAllTo: `/documents/shared?search=${encoded}`,
        items: docs.map((d) => mapDocumentItem(d)),
      });
    }
  }
  if (byMeRes.status === 'fulfilled') {
    const docs = pickList(byMeRes.value?.data?.data || byMeRes.value?.data, [
      'documents',
      'items',
      'data',
    ]).slice(0, PER_MODULE_LIMIT);
    if (docs.length) {
      groups.push({
        key: 'shared-by-me',
        title: 'Shared By Me',
        viewAllTo: `/documents/shared?search=${encoded}`,
        items: docs.map((d) => mapDocumentItem(d)),
      });
    }
  }
  return asGroups(groups);
}

export async function searchFavoriteDocumentsModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/documents/favorites', {
      params: { search: q, corporate_id: corporateId, limit: PER_MODULE_LIMIT, page: 1 },
    });
    const docs = flattenFavoriteDocuments(res?.data).slice(0, PER_MODULE_LIMIT);
    if (!docs.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'favorites',
          title: 'Favorite Documents',
          viewAllTo: `/documents/favorites?search=${encoded}`,
          items: docs.map((d) => mapDocumentItem(d)),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchDocumentClassificationsModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/document-categories', {
      params: { corporate_id: corporateId },
    });
    const categories = pickList(res?.data, ['data', 'categories']).filter((c) =>
      matchesQuery(c.name || c.category_name || c.title, q)
    ).slice(0, PER_MODULE_LIMIT);
    if (!categories.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'classifications',
          title: 'Classifications',
          viewAllTo: `/documents/documentclassifications?search=${encoded}`,
          items: categories.map((c) => ({
            id: c.id,
            title: c.name || c.category_name || c.title || 'Classification',
            subtitle: c.parent?.name || '',
            to: `/documents/documentclassifications?search=${encoded}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchVaultModule(query, pathname = '') {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const encoded = encodeURIComponent(q);
  const isPersonal = String(pathname || '').toLowerCase().includes('personalvaultlist');
  const scope = isPersonal ? 'personal' : 'corporate';
  const listPath = isPersonal
    ? '/credential-vault/personalVaultList'
    : '/credential-vault/credentialVaultList';
  try {
    const res = await axiosInstance.get('/credential-vault', {
      params: { search: q, limit: PER_MODULE_LIMIT, page: 1, scope },
    });
    const credentials = pickList(res?.data, ['credentials', 'items', 'data']).slice(
      0,
      PER_MODULE_LIMIT
    );
    if (!credentials.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'vault',
          title: isPersonal ? 'Personal Credentials' : 'Corporate Credentials',
          viewAllTo: `${listPath}?search=${encoded}`,
          items: credentials.map((c) => ({
            id: c.id,
            title: c.title || c.name || c.username || 'Credential',
            subtitle: c.category || c.scope || '',
            to: `/credential-vault/credentialDetail/${c.id}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchWorkRegisterModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/work-register', { params: { date: todayYmd() } });
    const items = pickList(res?.data?.data || res?.data, ['items', 'data'])
      .filter((row) =>
        matchesQuery(row.description, q) ||
        matchesQuery(row.type, q) ||
        matchesQuery(row.status_label || row.status, q)
      )
      .slice(0, PER_MODULE_LIMIT);
    if (!items.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'work-register',
          title: 'Work Register',
          viewAllTo: `/work-register/list?search=${encoded}`,
          items: items.map((row, index) => ({
            id: row.id || row.task_id || index,
            title: row.description || 'Work register entry',
            subtitle: [row.type, row.status_label || row.status].filter(Boolean).join(' · '),
            to: row.link && String(row.link).startsWith('/') ? row.link : `/work-register/list?search=${encoded}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchUsersModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/list/corporateuserslist', {
      params: { corporateId, CorporateId: corporateId, search: q, page: 1, limit: PER_MODULE_LIMIT },
    });
    const users = pickList(res?.data, ['users', 'data']).slice(0, PER_MODULE_LIMIT);
    if (!users.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'users',
          title: 'Users',
          viewAllTo: `/settings/userlist?search=${encoded}`,
          items: users.map((u) => ({
            id: u.id,
            title: u.full_name || u.name || u.email || 'User',
            subtitle: [u.email, u.department_name, u.branch_name].filter(Boolean).join(' · '),
            to: `/settings/userlist?search=${encoded}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchStaffModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get(`/staff/corporate/${corporateId}`, {
      params: { search: q, page: 1, limit: PER_MODULE_LIMIT },
    });
    const staff = pickList(res?.data, ['data', 'staff']).slice(0, PER_MODULE_LIMIT);
    if (!staff.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'staff',
          title: 'Staff',
          viewAllTo: `/staff/staffList?search=${encoded}`,
          items: staff.map((s) => ({
            id: s.id,
            title: [s.first_name, s.middle_name, s.last_name].filter(Boolean).join(' ') || s.employee_code || 'Staff',
            subtitle: [s.employee_code, s.Branch?.name, s.Department?.name].filter(Boolean).join(' · '),
            to: `/staff/staffDetails/${s.id}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchVehiclesModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/vehicle/getVehicleList', {
      params: { corporate_id: corporateId, search: q, page: 1, limit: PER_MODULE_LIMIT },
    });
    const vehicles = pickList(res?.data, ['data', 'vehicles']).slice(0, PER_MODULE_LIMIT);
    if (!vehicles.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'vehicles',
          title: 'Vehicles',
          viewAllTo: `/vehicle/vehicleList?search=${encoded}`,
          items: vehicles.map((v) => ({
            id: v.id,
            title: v.vehicle_name || v.name || v.registration_number || 'Vehicle',
            subtitle: [v.registration_number, v.vehicle_type].filter(Boolean).join(' · '),
            to: `/vehicle/vehicleDetails/${v.id}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

async function searchNamedSettingsList({
  query,
  request,
  pickKeys,
  titleKey,
  subtitleKeys = [],
  viewAllTo,
  itemTo,
  groupKey,
  groupTitle,
}) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await request();
    const rows = pickList(res?.data, pickKeys).filter((row) => {
      const hay = [row[titleKey], ...subtitleKeys.map((k) => row[k])].join(' ');
      return matchesQuery(hay, q);
    }).slice(0, PER_MODULE_LIMIT);
    if (!rows.length) return { groups: [] };
    return {
      groups: [
        {
          key: groupKey,
          title: groupTitle,
          viewAllTo: `${viewAllTo}?search=${encoded}`,
          items: rows.map((row) => ({
            id: row.id,
            title: row[titleKey] || groupTitle,
            subtitle: subtitleKeys.map((k) => row[k]).filter(Boolean).join(' · '),
            to: itemTo ? itemTo(row, encoded) : `${viewAllTo}?search=${encoded}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

export async function searchBranchesModule(query) {
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  return searchNamedSettingsList({
    query,
    request: () => axiosInstance.get('/settings/branches', { params: { corporate_id: corporateId } }),
    pickKeys: ['data'],
    titleKey: 'name',
    subtitleKeys: ['location'],
    viewAllTo: '/settings/branchList',
    itemTo: (row) => `/settings/editBranch/${row.id}`,
    groupKey: 'branches',
    groupTitle: 'Branches',
  });
}

export async function searchDepartmentsModule(query) {
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  return searchNamedSettingsList({
    query,
    request: () => axiosInstance.get('/settings/departments', { params: { corporate_id: corporateId } }),
    pickKeys: ['data'],
    titleKey: 'name',
    subtitleKeys: [],
    viewAllTo: '/settings/departmentList',
    itemTo: (row) => `/settings/editDepartment/${row.id}`,
    groupKey: 'departments',
    groupTitle: 'Departments',
  });
}

export async function searchSubDepartmentsModule(query) {
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  return searchNamedSettingsList({
    query,
    request: () => axiosInstance.get('/settings/subDepartments', { params: { corporate_id: corporateId } }),
    pickKeys: ['data'],
    titleKey: 'name',
    subtitleKeys: [],
    viewAllTo: '/settings/subDepartmentList',
    itemTo: (row) => `/settings/editSubDepartment/${row.id}`,
    groupKey: 'sub-departments',
    groupTitle: 'Sub-Departments',
  });
}

export async function searchDesignationsModule(query) {
  const corporateId = getCorporateId();
  if (!corporateId) return { groups: [] };
  return searchNamedSettingsList({
    query,
    request: () => axiosInstance.get('/settings/getDesignationList', { params: { corporate_id: corporateId } }),
    pickKeys: ['data'],
    titleKey: 'name',
    subtitleKeys: [],
    viewAllTo: '/settings/designationList',
    itemTo: (row) => `/settings/editDesignation/${row.id}`,
    groupKey: 'designations',
    groupTitle: 'Designations',
  });
}

export async function searchModulesModule(query) {
  const q = String(query || '').trim();
  if (q.length < 2) return { groups: [] };
  const encoded = encodeURIComponent(q);
  try {
    const res = await axiosInstance.get('/modules/list');
    const modules = asArray(res?.data)
      .filter((m) => matchesQuery(m.ModuleName || m.name || m.display_name, q))
      .slice(0, PER_MODULE_LIMIT);
    if (!modules.length) return { groups: [] };
    return {
      groups: [
        {
          key: 'modules',
          title: 'Modules',
          viewAllTo: `/modules?search=${encoded}`,
          items: modules.map((m) => ({
            id: m.id || m.ModuleId || m.ModuleName,
            title: m.ModuleName || m.name || m.display_name || 'Module',
            subtitle: m.description || '',
            to: `/modules?search=${encoded}`,
          })),
        },
      ],
    };
  } catch {
    return { groups: [] };
  }
}

/**
 * Run navbar search for the current page scope (or all modules on dashboard).
 */
export async function searchByScope(scope, query, { pathname } = {}) {
  switch (scope) {
    case 'tasks':
      return searchTasksModule(query);
    case 'worksheet':
      return searchWorksheetModule(query);
    case 'projects':
      return searchProjectsModule(query);
    case 'forum':
      return searchForumModule(query);
    case 'documents':
      return searchDocumentsModule(query);
    case 'documents-shared':
      return searchSharedDocumentsModule(query);
    case 'documents-favorites':
      return searchFavoriteDocumentsModule(query);
    case 'documents-classifications':
      return searchDocumentClassificationsModule(query);
    case 'vault':
      return searchVaultModule(query, pathname);
    case 'work-register':
      return searchWorkRegisterModule(query);
    case 'users':
      return searchUsersModule(query);
    case 'staff':
      return searchStaffModule(query);
    case 'vehicles':
      return searchVehiclesModule(query);
    case 'branches':
      return searchBranchesModule(query);
    case 'departments':
      return searchDepartmentsModule(query);
    case 'sub-departments':
      return searchSubDepartmentsModule(query);
    case 'designations':
      return searchDesignationsModule(query);
    case 'modules':
      return searchModulesModule(query);
    default:
      return searchAcrossModules(query);
  }
}
